/*
 * 预订设置里的「后台营业时间」和「实际时间」互换。
 *
 * 云端存的营业时段、包厢特殊价格的时间都是后台营业时间：后台时间 = 实际时间 − businessHourOffset（小时）。
 * 比如 offset = 6 时，后台 00:00 = 实际 06:00，后台 23:59 = 实际次日 05:59。
 * 页面上按实际时间显示和选择，存的时候仍然是后台时间（选择框的值就是后台时间，只是显示成实际时间）。
 * offset 取不到时（null）不做换算，按后台时间原样显示，并标明「后台时间」。
 */
import { checkReservationStatus } from '@/model/dataLayer/api/newReservation/reservation'
import i18n from '@/i18n'

const DAY_MINUTES = 24 * 60

/** "06:00:00" / "6:00" / "06:00" → "06:00"；不认识的返回 null */
export function normalizeHHmm (t) {
  if (!t || typeof t !== 'string') return null
  const m = t.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return String(h).padStart(2, '0') + ':' + String(min).padStart(2, '0')
}

export function toMinutes (t) {
  const n = normalizeHHmm(t)
  if (!n) return null
  const [h, m] = n.split(':').map(Number)
  return h * 60 + m
}

function fromMinutes (total) {
  const v = ((total % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES
  return String(Math.floor(v / 60)).padStart(2, '0') + ':' + String(v % 60).padStart(2, '0')
}

/**
 * 后台时间 → 实际时间
 * @return {{time: string, dayShift: number}|null} dayShift = 1 表示实际时间是次日，-1 表示前一天
 */
export function backendToActual (t, offset) {
  const mins = toMinutes(t)
  if (mins === null) return null
  const total = mins + (offset || 0) * 60
  return { time: fromMinutes(total), dayShift: Math.floor(total / DAY_MINUTES) }
}

/** 按实际时间显示一个后台时间；offset 为 null 时原样显示 */
export function actualLabel (t, offset) {
  if (offset === null || offset === undefined) return normalizeHHmm(t) ?? (t || '')
  const a = backendToActual(t, offset)
  if (!a) return t || ''
  if (a.dayShift > 0) return i18n.t('reserveTimeNextDay', { time: a.time })
  if (a.dayShift < 0) return i18n.t('reserveTimePrevDay', { time: a.time })
  return a.time
}

/** 一段后台时间 [start, end) 有多少分钟；end ≤ start 时返回 0 */
export function spanMinutes (start, end) {
  const s = toMinutes(start)
  const e = toMinutes(end)
  if (s === null || e === null || e <= s) return 0
  return e - s
}

/**
 * 时间选择项（值 = 后台时间，按 15 分钟一档，最后加 23:59 表示营业日结束），显示成实际时间。
 * 按后台时间排序，所以列表顺序就是营业日里的先后顺序（跨午夜的排在后面，标「次日」）。
 * extra：当前值不在档位里（比如老数据 10:10）时也放进去，免得选择框显示空白。
 */
export function businessTimeItems (offset, extra = []) {
  const values = new Set()
  for (let m = 0; m < DAY_MINUTES; m += 15) values.add(fromMinutes(m))
  values.add('23:59')
  extra.map(normalizeHHmm).filter(Boolean).forEach(v => values.add(v))
  return [...values]
    .sort((a, b) => toMinutes(a) - toMinutes(b))
    .map(v => ({ value: v, text: actualLabel(v, offset) }))
}

/**
 * 读门店的营业日偏移（小时）。读不到返回 null（页面按后台时间原样显示）。
 */
export async function loadBusinessHourOffset () {
  try {
    const status = await checkReservationStatus()
    const offset = status?.setting?.businessHourOffset
    if (offset === null || offset === undefined || offset === '') return null
    const n = Number(offset)
    return Number.isFinite(n) ? n : null
  } catch (e) {
    return null
  }
}

export const WEEKDAY_ZH = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

/** "yyyy-MM-dd" → 1（周一）…7（周日） */
export function isoWeekday (date) {
  const d = new Date(date + 'T00:00:00')
  if (isNaN(d.getTime())) return null
  const js = d.getDay()
  return js === 0 ? 7 : js
}
